import { useInterviewStatus } from '../hooks/useBuildGameInterview';
import { STALE_PROGRESS_THRESHOLD_MS } from '@volley/crucible-ipc-types/build-game-constants';
import type { ProjectRef } from '@volley/crucible-ipc-types';

/**
 * Dashboard game-card pill mirroring `<TemplateUpdateBadge>` (#135).
 * Three states:
 *
 *   - In-progress + fresh (`progressAt` < STALE_PROGRESS_THRESHOLD_MS old):
 *     sky-blue "Interview: 4/9" pill. Click → open Workspace.
 *   - In-progress + stale (`progressAt` ≥ threshold): amber
 *     "Interview waiting" pill, same click target.
 *   - Anything else (no interview, complete, dispatched, building,
 *     built, abandoned): renders nothing.
 *
 * Codex round 4 §1: the IPC handler re-reads `progressAt` from disk
 * on every call. React Query's `refetchOnMount: 'always'` +
 * `refetchOnWindowFocus: 'always'` (in the hook) plus the 60-second
 * `staleTime` mean the badge tracks the lock model without drift.
 */

export function InterviewInProgressBadge({
  gameId,
  project,
  onClick,
  enabled,
}: {
  gameId: string;
  project: ProjectRef;
  onClick: (e: React.MouseEvent) => void;
  /**
   * Dashboard lazy-load path (`docs/dashboard-lazy-load-plan.md`
   * lines 109-140): set to `false` while the card is below the fold so
   * the 5-second `build-game:interview-status` poll only runs for
   * visible cards. Otherwise a 110-card grid would sustain
   * `110 / 5 = 22 reqs/sec` against the IPC even when idle. Defaults
   * `true` for non-Dashboard call sites.
   */
  enabled?: boolean;
}): React.JSX.Element | null {
  const { data } = useInterviewStatus(gameId, { enabled, project });
  if (!data || data.status !== 'in-progress') return null;

  const ageMs = Date.now() - new Date(data.progressAt).getTime();
  const isStale = ageMs >= STALE_PROGRESS_THRESHOLD_MS;

  const tone = isStale
    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
    : 'bg-sky-500/20 text-sky-300 hover:bg-sky-500/30';

  const label = isStale
    ? 'Interview waiting'
    : `Interview: ${data.answeredCount}/${data.totalCount}`;

  const tooltip = isStale
    ? `An interview's been open here for a while (last activity ${formatAge(ageMs)} ago). Click to pick it up.`
    : `${data.answeredCount} of ${data.totalCount} questions answered. Click to continue the interview.`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        }
      }}
      title={tooltip}
      data-testid="interview-in-progress-badge"
      data-stale={isStale ? 'true' : 'false'}
      className={`shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${tone}`}
    >
      {label}
    </div>
  );
}

function formatAge(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `${hours} h`;
}
